import React, { useEffect, useState } from "react";
import { ICustomerUser } from "../models/ICustomerUser";
import { IUserRole } from "../models/IUserRoles";
import { getUserListByRoleIds, getUserRoles, removeUser } from "../services/auth";
import {
  LIVE_CHAT_AGENT_ROLE_ID, RESTAURANT_ADMIN_ROLE_ID, RESTAURANT_DRIVER_ROLE_ID, SUPER_ADMIN_ROLE_ID,
} from "../services/checkRole";
import CreateUserModal from "./CreateUserModal";
import UserRoleAssigner from "./UserRoleAssigner";
import UserRestaurantLinks from "./UserRestaurantLinks";

const DEFAULT_ROLE_ID = "0cbe4ee5-2b6c-ef11-adfb-5bef2cd6e8d8";

/** Kullanıcı listesi yanıtı rol taşımıyor; her kullanıcının rolleri ayrıca çekilir. */
export type RolesState = IUserRole[] | "loading" | "error";

const ROLE_STYLE: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  [SUPER_ADMIN_ROLE_ID]:       { label: "Süper Admin",         icon: "bi-shield-fill-check", color: "#7c3aed", bg: "#f3e8ff" },
  [RESTAURANT_ADMIN_ROLE_ID]:  { label: "Restoran Yöneticisi", icon: "bi-shop",             color: "#4338ca", bg: "#e0e7ff" },
  [RESTAURANT_DRIVER_ROLE_ID]: { label: "Kurye",               icon: "bi-bicycle",          color: "#c2410c", bg: "#ffedd5" },
  [LIVE_CHAT_AGENT_ROLE_ID]:   { label: "Canlı Destek",        icon: "bi-headset",          color: "#0f766e", bg: "#ccfbf1" },
};

function roleView(r: IUserRole) {
  return ROLE_STYLE[r.roleId] ?? {
    label: r.role?.roleName || "Bilinmeyen rol",
    icon: "bi-person-badge",
    color: "#475569",
    bg: "#f1f5f9",
  };
}

/** Aynı anda en fazla `limit` istekle her kullanıcının rollerini çeker. */
export async function fetchRolesInBatches(
  userIds: string[],
  limit: number,
  onResult: (userId: string, roles: RolesState) => void,
) {
  let index = 0;
  const worker = async () => {
    while (index < userIds.length) {
      const id = userIds[index++];
      try {
        const res = await getUserRoles(id);
        onResult(id, res.data ?? []);
      } catch {
        onResult(id, "error");
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, userIds.length) }, worker));
}

export function getInitials(name: string, surname: string) {
  return `${name?.[0] ?? ""}${surname?.[0] ?? ""}`.toUpperCase();
}

function formatDate(dateStr: string) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("tr-TR");
}

export default function RestaurantUsersTab() {
  const [users, setUsers] = useState<ICustomerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<ICustomerUser | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<ICustomerUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [rolesByUser, setRolesByUser] = useState<Record<string, RolesState>>({});

  const loadRoles = (list: ICustomerUser[]) => {
    const ids = list.map((u) => u.id);
    setRolesByUser(Object.fromEntries(ids.map((id) => [id, "loading" as RolesState])));
    fetchRolesInBatches(ids, 6, (id, roles) => setRolesByUser((prev) => ({ ...prev, [id]: roles })));
  };

  // Rol atandıktan sonra yalnız o kullanıcının rolleri yeniden çekilir (liste ve popup birlikte güncellenir).
  const refreshUserRoles = async (userId: string) => {
    try {
      const res = await getUserRoles(userId);
      setRolesByUser((prev) => ({ ...prev, [userId]: res.data ?? [] }));
    } catch {
      setRolesByUser((prev) => ({ ...prev, [userId]: "error" }));
    }
  };

  const fetchUsers = () => {
    setLoading(true);
    setError(null);
    getUserListByRoleIds([DEFAULT_ROLE_ID])
      .then((res) => {
        setUsers(res.data);
        loadRoles(res.data ?? []);
      })
      .catch(() => setError("Kullanıcılar yüklenirken bir hata oluştu."))
      .finally(() => setLoading(false));
  };

  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await removeUser(confirmDeleteUser.id);
      setUsers(prev => prev.filter(u => u.id !== confirmDeleteUser.id));
      setConfirmDeleteUser(null);
    } catch {
      setDeleteError("Kullanıcı silinemedi. Lütfen tekrar deneyin.");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = users.filter((u) => {
    const q = search.toLocaleLowerCase("tr");
    const roles = rolesByUser[u.id];
    const roleText = Array.isArray(roles) ? roles.map((r) => roleView(r).label).join(" ") : "";
    return [u.customerName, u.customerSurName, u.customerEmail, u.customerTelNo, roleText]
      .some((s) => (s ?? "").toLocaleLowerCase("tr").includes(q));
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div style={{
          background: "#eef2ff", borderRadius: 12, padding: "8px 16px",
          fontWeight: 700, color: "#4f46e5", fontSize: "0.85rem"
        }}>
          Toplam: {users.length} kullanıcı
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: "8px 18px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg, #6366f1, #4f46e5)",
            color: "#fff", fontWeight: 700, fontSize: "0.85rem",
            cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
            boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
          }}
        >
          <i className="bi bi-person-plus-fill"></i> Kullanıcı Yarat
        </button>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 20, maxWidth: 400 }}>
        <i className="bi bi-search" style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
          color: "#9ca3af", fontSize: "0.9rem"
        }}></i>
        <input
          type="text"
          placeholder="İsim, e-posta, telefon veya rol ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "10px 12px 10px 36px",
            border: "1.5px solid #e5e7eb", borderRadius: 10,
            fontSize: "0.88rem", outline: "none", background: "#fff",
            boxSizing: "border-box"
          }}
        />
      </div>

      {/* States */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>
          <div className="spinner-border text-primary" role="status" style={{ width: 36, height: 36 }} />
          <p style={{ marginTop: 12, fontSize: "0.9rem" }}>Kullanıcılar yükleniyor...</p>
        </div>
      )}

      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
          padding: "16px 20px", color: "#dc2626", display: "flex", alignItems: "center", gap: 10
        }}>
          <i className="bi bi-exclamation-triangle-fill"></i> {error}
        </div>
      )}

      {/* User Cards */}
      {!loading && !error && (
        <>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
              <i className="bi bi-person-x" style={{ fontSize: "2.5rem" }}></i>
              <p style={{ marginTop: 12 }}>Kullanıcı bulunamadı.</p>
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16
            }}>
              {filtered.map((user) => (
                <div key={user.id}
                  onClick={() => setSelectedUser(user)}
                  style={{
                    background: "#fff",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: 16,
                    padding: "20px",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s, border-color 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)"
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 6px 20px rgba(99,102,241,0.15)";
                    (e.currentTarget as HTMLDivElement).style.borderColor = "#c7d2fe";
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
                    (e.currentTarget as HTMLDivElement).style.borderColor = "#e5e7eb";
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
                    {/* Avatar */}
                    <div style={{
                      width: 46, height: 46, borderRadius: "50%",
                      background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                      color: "#fff", display: "flex", alignItems: "center",
                      justifyContent: "center", fontWeight: 800, fontSize: "1rem",
                      flexShrink: 0
                    }}>
                      {getInitials(user.customerName, user.customerSurName)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {user.customerName} {user.customerSurName}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {user.customerEmail || "-"}
                      </div>
                    </div>
                    {/* Status badge */}
                    <span style={{
                      fontSize: "0.7rem", fontWeight: 700, padding: "3px 10px",
                      borderRadius: 20, flexShrink: 0,
                      background: user.customerIsEnabled ? "#dcfce7" : "#fee2e2",
                      color: user.customerIsEnabled ? "#16a34a" : "#dc2626"
                    }}>
                      {user.customerIsEnabled ? "Aktif" : "Pasif"}
                    </span>
                    {/* Delete button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteUser(user); setDeleteError(""); }}
                      style={{
                        flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
                        border: "none", background: "#fee2e2", color: "#dc2626",
                        cursor: "pointer", display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: "0.8rem",
                      }}
                      title="Kullanıcıyı sil"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <InfoRow icon="bi-telephone" label={user.customerTelNo || "-"} />
                    {user.restourantUser?.restourants?.restaurantName && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem" }}>
                        <i className="bi bi-shop" style={{ color: user.restourantUser.restourantUserIsEnabled ? "#6366f1" : "#9ca3af" }}></i>
                        <span style={{ color: user.restourantUser.restourantUserIsEnabled ? "#4f46e5" : "#9ca3af", fontWeight: 600 }}>
                          {user.restourantUser.restourants.restaurantName}
                        </span>
                        {!user.restourantUser.restourantUserIsEnabled && (
                          <span style={{ fontSize: "0.68rem", color: "#9ca3af" }}>(Pasif)</span>
                        )}
                      </div>
                    )}
                    <RoleBadges roles={rolesByUser[user.id]} />
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <VerBadge ok={user.verification?.emailVerified} label="E-posta" icon="bi-envelope-check" />
                      <VerBadge ok={user.verification?.customerTelNoVerified} label="Telefon" icon="bi-phone" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          roles={rolesByUser[selectedUser.id]}
          onRolesChanged={() => refreshUserRoles(selectedUser.id)}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* Delete Confirm */}
      {confirmDeleteUser && (
        <>
          <div onClick={() => !deleting && setConfirmDeleteUser(null)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(4px)", zIndex: 1040
          }} />
          <div style={{
            position: "fixed", top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            background: "#fff", borderRadius: 20, zIndex: 1050,
            width: "min(420px, 92vw)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden"
          }}>
            <div style={{ height: 5, background: "linear-gradient(90deg, #dc2626, #ef4444)" }} />
            <div style={{ padding: "28px 28px 24px", textAlign: "center" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: "#fef2f2", display: "inline-flex",
                alignItems: "center", justifyContent: "center", marginBottom: 16
              }}>
                <i className="bi bi-person-x-fill" style={{ fontSize: "1.5rem", color: "#dc2626" }}></i>
              </div>
              <h6 style={{ fontWeight: 800, color: "#111827", marginBottom: 6 }}>Kullanıcıyı Sil</h6>
              <p style={{ color: "#6b7280", fontSize: "0.88rem", margin: "0 0 6px" }}>
                <strong style={{ color: "#111827" }}>
                  {confirmDeleteUser.customerName} {confirmDeleteUser.customerSurName}
                </strong> adlı kullanıcıyı silmek istediğinize emin misiniz?
              </p>
              <p style={{ color: "#9ca3af", fontSize: "0.8rem", margin: "0 0 20px" }}>Bu işlem geri alınamaz.</p>
              {deleteError && (
                <div style={{
                  marginBottom: 14, padding: "8px 12px", borderRadius: 9, fontSize: "0.8rem",
                  background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <i className="bi bi-exclamation-circle-fill"></i>{deleteError}
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setConfirmDeleteUser(null)}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 10,
                    border: "1.5px solid #e5e7eb", background: "#fff",
                    color: "#374151", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer"
                  }}
                >
                  İptal
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: "10px", borderRadius: 10, border: "none",
                    background: deleting ? "#e5e7eb" : "#dc2626",
                    color: deleting ? "#9ca3af" : "#fff",
                    fontWeight: 700, fontSize: "0.88rem",
                    cursor: deleting ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  {deleting
                    ? <><span className="spinner-border spinner-border-sm" role="status" /> Siliniyor...</>
                    : <><i className="bi bi-trash3-fill"></i> Evet, Sil</>
                  }
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
}

function InfoRow({ icon, label }: { icon: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#4b5563", fontSize: "0.82rem" }}>
      <i className={`bi ${icon}`} style={{ color: "#9ca3af", width: 16, textAlign: "center" }}></i>
      {label}
    </div>
  );
}

function VerBadge({ ok, label, icon }: { ok: boolean; label: string; icon: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: "0.7rem", fontWeight: 600, padding: "3px 8px", borderRadius: 20,
      background: ok ? "#dcfce7" : "#f3f4f6",
      color: ok ? "#16a34a" : "#9ca3af"
    }}>
      <i className={`bi ${icon}`}></i> {label}
    </span>
  );
}

export function RoleBadges({ roles }: { roles?: RolesState }) {
  if (roles === undefined || roles === "loading") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.74rem", color: "#9ca3af" }}>
        <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} />Roller yükleniyor…
      </div>
    );
  }
  if (roles === "error") {
    return <div style={{ fontSize: "0.74rem", color: "#9ca3af" }}><i className="bi bi-exclamation-circle me-1"></i>Roller alınamadı</div>;
  }
  if (roles.length === 0) {
    return <div style={{ fontSize: "0.74rem", color: "#9ca3af" }}><i className="bi bi-person-badge me-1"></i>Rol atanmamış</div>;
  }
  // Aktif roller önce
  const sorted = roles.slice().sort((a, b) => Number(b.userRoleIsEnabled !== false) - Number(a.userRoleIsEnabled !== false));
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {sorted.map((r) => {
        const v = roleView(r);
        const inactive = r.userRoleIsEnabled === false;
        return (
          <span key={r.id} title={inactive ? "Pasif rol" : r.role?.roleDescription || v.label}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: "0.7rem", fontWeight: 700, padding: "3px 9px", borderRadius: 20,
              background: inactive ? "#f3f4f6" : v.bg, color: inactive ? "#9ca3af" : v.color,
              textDecoration: inactive ? "line-through" : "none",
            }}>
            <i className={`bi ${v.icon}`}></i>{v.label}
          </span>
        );
      })}
    </div>
  );
}

export function UserDetailModal({ user, roles, onRolesChanged, onClose }: {
  user: ICustomerUser;
  roles?: RolesState;
  onRolesChanged: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        backdropFilter: "blur(4px)", zIndex: 1040
      }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        background: "#fff", borderRadius: 20, zIndex: 1050,
        width: "min(560px, 95vw)", maxHeight: "85vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", padding: "28px"
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "linear-gradient(135deg, #6366f1, #4f46e5)",
            color: "#fff", display: "flex", alignItems: "center",
            justifyContent: "center", fontWeight: 800, fontSize: "1.2rem", flexShrink: 0
          }}>
            {getInitials(user.customerName, user.customerSurName)}
          </div>
          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: 800, color: "#111827" }}>
              {user.customerName} {user.customerSurName}
            </h5>
            <span style={{
              fontSize: "0.75rem", fontWeight: 700, padding: "2px 10px",
              borderRadius: 20, marginTop: 4, display: "inline-block",
              background: user.customerIsEnabled ? "#dcfce7" : "#fee2e2",
              color: user.customerIsEnabled ? "#16a34a" : "#dc2626"
            }}>
              {user.customerIsEnabled ? "Aktif" : "Pasif"}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: "#f3f4f6", border: "none", borderRadius: "50%",
            width: 36, height: 36, cursor: "pointer", color: "#6b7280",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem"
          }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Info Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          <DetailField label="E-posta" value={user.customerEmail || "-"} icon="bi-envelope" />
          <DetailField label="Telefon" value={user.customerTelNo || "-"} icon="bi-telephone" />
          <DetailField label="Kullanıcı Adı" value={user.customerUserName || "-"} icon="bi-person" />
          <DetailField label="Doğum Tarihi" value={formatDate(user.customerBirthDate)} icon="bi-calendar3" />
        </div>

        {/* Restoranlar — bir hesap birden çok restorana bağlanabilir */}
        <div style={{ background: "#f5f3ff", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
          <UserRestaurantLinks
            userId={user.id}
            userName={`${user.customerName ?? ""} ${user.customerSurName ?? ""}`.trim()}
          />
        </div>

        {/* Roller */}
        <div style={{ background: "#f8faff", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#374151", marginBottom: 10 }}>
            <i className="bi bi-person-badge-fill me-2" style={{ color: "#6366f1" }}></i>Roller
          </div>
          <RoleBadges roles={roles} />
          <UserRoleAssigner
            userId={user.id}
            userName={`${user.customerName ?? ""} ${user.customerSurName ?? ""}`.trim()}
            roles={roles}
            onChanged={onRolesChanged}
          />
        </div>

        {/* Verification */}
        <div style={{ background: "#f8faff", borderRadius: 12, padding: "14px 16px", marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#374151", marginBottom: 10 }}>
            <i className="bi bi-shield-check me-2" style={{ color: "#6366f1" }}></i>Doğrulama Durumu
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <VerBadge ok={user.verification?.emailVerified} label="E-posta Doğrulandı" icon="bi-envelope-check" />
            <VerBadge ok={user.verification?.customerTelNoVerified} label="Telefon Doğrulandı" icon="bi-phone" />
            <VerBadge ok={user.verification?.verificationIsEnabled} label="Doğrulama Aktif" icon="bi-toggle-on" />
          </div>
        </div>
      </div>
    </>
  );
}

function DetailField({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div style={{ background: "#f8faff", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: "0.72rem", color: "#9ca3af", fontWeight: 600, marginBottom: 3 }}>
        <i className={`bi ${icon} me-1`}></i>{label}
      </div>
      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#111827", wordBreak: "break-all" }}>
        {value}
      </div>
    </div>
  );
}
